import { useState } from "react";
import { Smartphone, Link2, Check, QrCode, Share2, Mail, MessageSquare, MessageCircle, Facebook, Linkedin, Send, Twitter } from "lucide-react";
import { toast } from "sonner";

/* InstallQrCard — scan-from-laptop shortcut to the install page.
   Two variants:
     "full"    — hero card with big QR + share sheet (native + WhatsApp/Email/SMS/X/Facebook/LinkedIn/Telegram + Copy).
     "compact" — inline strip with small QR + share button. For Home & footers.
   The QR always points to <SITE_URL>/install (falls back to travelerpulse.pro). */
const INSTALL_PATH = "/install";

function installUrl() {
  const base = (process.env.REACT_APP_SITE_URL || "").replace(/\/$/, "") ||
               (typeof window !== "undefined" ? window.location.origin : "https://travelerpulse.pro");
  return `${base}${INSTALL_PATH}`;
}

const SHARE_TEXT = "Install TravelerPulse — your complete travel life in one app. Add it to your home screen in 10 seconds:";
const EMAIL_SUBJECT = "Install TravelerPulse on your phone";

export default function InstallQrCard({ variant = "full", className = "" }) {
  const url = installUrl();
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Install link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error("Copy failed — long-press the link instead"); }
  };

  const canShare = typeof navigator !== "undefined" && typeof navigator.share === "function";
  const share = async () => {
    if (!canShare) return copy();
    try {
      await navigator.share({ title: EMAIL_SUBJECT, text: SHARE_TEXT, url });
    } catch (e) {
      if (e?.name !== "AbortError") copy();
    }
  };

  // Encoded once for reuse across share links
  const encUrl = encodeURIComponent(url);
  const encText = encodeURIComponent(`${SHARE_TEXT} ${url}`);
  const encTextOnly = encodeURIComponent(SHARE_TEXT);
  const encSubject = encodeURIComponent(EMAIL_SUBJECT);
  const encBody = encodeURIComponent(`${SHARE_TEXT}\n\n${url}`);

  const links = [
    { key: "whatsapp",  label: "WhatsApp", cls: "text-emerald-600 hover:bg-emerald-50 border-emerald-200",
      href: `https://wa.me/?text=${encText}`, Icon: MessageCircle },
    { key: "email",     label: "Email",    cls: "text-slate-700 hover:bg-slate-100 border-slate-300",
      href: `mailto:?subject=${encSubject}&body=${encBody}`, Icon: Mail },
    { key: "sms",       label: "SMS",      cls: "text-sky-700 hover:bg-sky-50 border-sky-200",
      href: `sms:?body=${encText}`, Icon: MessageSquare },
    { key: "x",         label: "X",        cls: "text-slate-900 hover:bg-slate-100 border-slate-300",
      href: `https://twitter.com/intent/tweet?text=${encTextOnly}&url=${encUrl}`, Icon: Twitter },
    { key: "facebook",  label: "Facebook", cls: "text-blue-700 hover:bg-blue-50 border-blue-200",
      href: `https://www.facebook.com/sharer/sharer.php?u=${encUrl}`, Icon: Facebook },
    { key: "linkedin",  label: "LinkedIn", cls: "text-sky-800 hover:bg-sky-50 border-sky-300",
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encUrl}`, Icon: Linkedin },
    { key: "telegram",  label: "Telegram", cls: "text-sky-600 hover:bg-sky-50 border-sky-200",
      href: `https://t.me/share/url?url=${encUrl}&text=${encTextOnly}`, Icon: Send },
  ];

  if (variant === "compact") {
    const qr = `https://api.qrserver.com/v1/create-qr-code/?size=120x120&margin=2&data=${encUrl}`;
    return (
      <section className={`flex items-center gap-4 p-4 rounded-2xl border border-slate-200 bg-white ${className}`}
        data-testid="install-qr-compact">
        <img src={qr} alt="Scan to install the TravelerPulse app on your phone"
          className="w-20 h-20 rounded-lg border border-slate-200 bg-white p-1 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-emerald-700 flex items-center gap-1">
            <QrCode className="w-3 h-3" /> Scan from your laptop
          </div>
          <div className="font-serif font-800 text-lg text-slate-900 leading-tight mt-0.5">Open the install page on your phone</div>
          <div className="text-xs text-slate-600 mt-1">Point your phone's camera at the code — the install guide opens instantly.</div>
        </div>
        <button onClick={share} data-testid="install-qr-share-compact"
          className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-700 rounded shrink-0">
          <Share2 className="w-3.5 h-3.5" /> {canShare ? "Share" : "Copy link"}
        </button>
      </section>
    );
  }

  const qr = `https://api.qrserver.com/v1/create-qr-code/?size=280x280&margin=2&data=${encUrl}`;
  return (
    <section className={`rounded-2xl border-2 border-emerald-300 bg-gradient-to-br from-emerald-50 to-white p-5 sm:p-7 grid sm:grid-cols-[220px_1fr] gap-5 items-center ${className}`}
      data-testid="install-qr-full">
      <img src={qr} alt="Scan to install the TravelerPulse app on your phone"
        className="w-44 sm:w-52 h-44 sm:h-52 rounded-xl border border-emerald-200 bg-white p-2 shadow-sm justify-self-center" />
      <div>
        <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-emerald-700 inline-flex items-center gap-1.5">
          <Smartphone className="w-3.5 h-3.5" /> Scan &amp; install in 10 seconds
        </div>
        <h2 className="font-serif font-800 text-2xl sm:text-3xl text-slate-900 mt-2 leading-tight">
          On your laptop? <br className="hidden sm:block" />Scan this on your phone.
        </h2>
        <p className="text-sm text-slate-700 mt-2 leading-relaxed max-w-md">
          Point your phone's camera at the QR code — or share the install page with friends and family below.
        </p>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <button onClick={share} data-testid="install-qr-share"
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-700 rounded-full">
            <Share2 className="w-4 h-4" /> {canShare ? "Share install link" : "Copy install link"}
          </button>
          {canShare && (
            <button onClick={copy} data-testid="install-qr-copy"
              className="inline-flex items-center gap-1.5 px-4 py-2 border-2 border-emerald-300 hover:border-emerald-500 text-emerald-800 text-sm font-700 rounded-full">
              {copied ? <Check className="w-4 h-4" /> : <Link2 className="w-4 h-4" />} {copied ? "Link copied" : "Copy link"}
            </button>
          )}
        </div>

        {/* Per-channel share sheet — works on every device, no login required */}
        <div className="mt-3">
          <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-slate-500 mb-1.5">Or share via</div>
          <div className="flex flex-wrap gap-1.5" data-testid="install-share-sheet">
            {links.map(({ key, label, cls, href, Icon }) => (
              <a key={key} href={href} target="_blank" rel="noopener noreferrer"
                data-testid={`install-share-${key}`}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 border rounded-full bg-white text-xs font-700 transition ${cls}`}>
                <Icon className="w-3.5 h-3.5" /> {label}
              </a>
            ))}
          </div>
        </div>

        <div className="mt-3 text-[11px] text-slate-500 font-mono truncate max-w-[280px]" title={url}>{url}</div>
      </div>
    </section>
  );
}
